import { reactive, watch, onCleanup, onMount, untrack } from 'cdui-js';
import { getAuthFileBlobUrl, revokeAuthFileBlobUrl } from '../../../lib/authFileBlob';
import { DiFiPreview } from '../../../components/DiFi/DiFiPreview';
import { DiFiLoading } from '../../../components/DiFi/DiFiLoading';

interface Props {
  /** 相对路径（如 upload/...），或本会话 blob: URL */
  path?: string;
  class?: any;
  style?: any;
  /** 受控：是否打开大图预览 */
  previewOpen?: boolean;
  /** 关闭大图预览 */
  onPreviewClose?: () => void;
  /** blob URL 就绪/清空时回调（供外部预览按钮判断） */
  onUrlReady?: (url: string) => void;
  /** 为 false 时不挂载内置 DiFiPreview（由页面级多图预览接管） */
  enablePreview?: boolean;
}

/**
 * 敏感图片鉴权预览（/file/content → blob）。
 * 本会话 blob: path 直接回显，不请求 content。
 */
export const AuthSensitiveImage = (props: Props) => {
  const state = reactive({
    url: '',
    loading: false,
  });

  /** 当前已加载/加载中的 path，用于短路 */
  let loadedPath = '';
  /** 递增序号，丢弃过期异步结果 */
  let loadSeq = 0;
  /** 是否由本组件 createObjectURL 产生（父传入的 blob: 不 revoke） */
  let ownedBlob = false;

  /**
   * 按 path 加载预览；同 path 短路；watch 内须 untrack，避免读 state.url 形成死循环。
   * @param path 相对路径或 blob URL
   */
  const load = async (path?: string) => {
    const next = String(path || '').trim();
    const curUrl = untrack(() => state.url);
    const curLoading = untrack(() => state.loading);
    if (next && next === loadedPath && (curUrl || curLoading)) {
      return;
    }

    const seq = ++loadSeq;
    if (ownedBlob) {
      revokeAuthFileBlobUrl(curUrl);
    }
    ownedBlob = false;
    loadedPath = next;
    untrack(() => {
      state.url = '';
    });
    props.onUrlReady?.('');

    if (!next) {
      state.loading = false;
      return;
    }

    state.loading = true;
    try {
      const url = await getAuthFileBlobUrl(next);
      if (seq !== loadSeq) {
        // 过期结果：仅回收本轮新创建的 blob
        if (url.startsWith('blob:') && !next.startsWith('blob:')) {
          revokeAuthFileBlobUrl(url);
        }
        return;
      }
      ownedBlob = url.startsWith('blob:') && !next.startsWith('blob:');
      state.url = url;
      props.onUrlReady?.(state.url);
    } catch (e) {
      if (seq !== loadSeq) {
        return;
      }
      console.error('AuthSensitiveImage load failed', e);
      state.url = '';
      loadedPath = '';
      props.onUrlReady?.('');
    } finally {
      if (seq === loadSeq) {
        state.loading = false;
      }
    }
  };

  onMount(() => {
    void untrack(() => load(props.path));
  });

  watch(
    () => props.path,
    (p) => {
      // untrack：load 内读写 state 不得成为本 watch 依赖，否则会无限请求 /file/content
      void untrack(() => load(p));
    },
  );

  watch(
    () => props.previewOpen,
    (open) => {
      if (props.enablePreview === false) return;
      if (!open) return;
      if (!untrack(() => state.url)) props.onPreviewClose?.();
    },
  );

  onCleanup(() => {
    if (ownedBlob) {
      revokeAuthFileBlobUrl(untrack(() => state.url));
    }
  });

  return (
    <>
      {state.url ? (
        <img class={props.class} style={props.style} src={state.url} />
      ) : state.loading ? (
        <div
          class={props.class}
          style={{
            display: 'flex',
            'align-items': 'center',
            'justify-content': 'center',
            width: '100%',
            height: '100%',
            ...(props.style || {}),
          }}
        >
          <DiFiLoading overlay={false} size="sm" />
        </div>
      ) : null}

      {props.enablePreview !== false ? (
        <DiFiPreview
          visible={!!props.previewOpen && !!state.url}
          src={state.url}
          zIndex={10000}
          onClose={() => props.onPreviewClose?.()}
        />
      ) : null}
    </>
  );
};
